import React from "react";
import { ToastBar, Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { X } from "lucide-react";

import { logoutFromHelper } from "api/Helper";
import { ROUTES } from "utils/routes";

import { logout } from "../redux/auth/AuthSlice";
import { store } from "../redux/Store";
import { removeuser } from "../redux/user/userSlice";

const handle403 = (error) => {
  if (error?.response?.status === 403) {
    logoutFromHelper();
    store.dispatch(logout());
    store.dispatch(removeuser());
    // Same-tab identity switch (e.g. two staff testing back-to-back) would
    // otherwise leave the previous user's cached queries in memory for the
    // next login to read — see useStaffInfo.
    queryClient.clear();
    window.location.href = ROUTES.AUTH.LOGOUT;
    return true;
  }
  return false;
};

const handleGlobalMutationError = (error, _variables, _context, mutation) => {
  if (handle403(error)) return;

  // Skip toast if the mutation handles errors itself (v5: mutation.meta, v4: mutation.options.meta)
  if (
    mutation?.meta?.skipGlobalError ??
    mutation?.options?.meta?.skipGlobalError
  )
    return;

  console.error("Mutation error:", error);
  toast.error("An error occurred. Please try again.");
};

// A failed read reports itself where the data was going to be — the table or
// panel renders a "could not load" state with a retry. One screen fires many
// queries, so toasting reads as well would stack a dozen identical toasts over
// the very message the user is meant to read. Writes still toast: a failed save
// has no place on the page to appear.
//
// `meta.errorMessage` is kept as the per-query label for that in-place state.
const handleGlobalQueryError = (error, query) => {
  if (handle403(error)) return;

  console.error(
    `Query error${query?.meta?.errorMessage ? ` (${query.meta.errorMessage})` : ""}:`,
    error
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
  queryCache: new QueryCache({ onError: handleGlobalQueryError }),
  mutationCache: new MutationCache({ onError: handleGlobalMutationError }),
});

const muiTheme = createTheme({
  components: {
    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          popper: {
            sx: { zIndex: 1400 },
          },
        },
      },
    },
  },
});

/**
 * Global layout - wraps the entire app with providers and global UI.
 * - QueryClientProvider (React Query)
 * - Toaster (react-hot-toast)
 * Renders children via props.
 */
const GlobalLayout = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={muiTheme}>
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 200000 }}
        toastOptions={{
          duration: 5000,
          error: { duration: 5000 },
          success: { duration: 5000 },
          closeButton: true,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0 0 0 8px",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    color: "inherit",
                    opacity: 0.6,
                  }}
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </button>
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
      {children}
    </ThemeProvider>
  </QueryClientProvider>
);

export { GlobalLayout, queryClient };
